export interface ILogicalExpression extends Array<string | number | ILogicalExpression> {}

// Single source of truth for the RQL operator names — shared by the tokenizer below, the
// content validator (equationValidation.ts), and the CodeMirror keyword highlighter
// (rqlLanguage.ts), so all three can never drift out of sync with each other.
export const RQL_OPERATORS = ['AND', 'QAND', 'OR', 'QOR', 'EQUAL', 'NOTEQUAL', 'COMPARE', 'QHASANSWER', 'QHASNOTANSWER', 'IMPORT'] as const;

// Quote-aware whitespace stripper — collapses a pretty-printed (multi-line, indented) equation
// down to one line, without touching whitespace inside a quoted comparison value (e.g. the
// space in "Passed First Chamber"). Ported verbatim from mn-api-helpers's equationParser.ts
// (used there to minify before execution); here it's purely a display concern — e.g. the Dry
// Run modal shows the equation on one line instead of the multi-line Prettify format.
export const minifyEquation = (equation: string): string => {
    let result = '';
    let quoteChar: string | null = null;
    for (let i = 0; i < equation.length; i++) {
        const char = equation[i];
        if (quoteChar) {
            result += char;
            if (char === quoteChar) {
                quoteChar = null;
            }
            continue;
        }
        if (char === '"' || char === "'") {
            quoteChar = char;
            result += char;
            continue;
        }
        if (/\s/.test(char)) {
            continue;
        }
        result += char;
    }
    return result;
};

interface ISyntaxCheckResult {
    index: number;
    message: string;
}

export class EquationParser{
    public equationRaw: string;
    private allTokens: string[];
    private tokens: string[];
    public equationStack: ILogicalExpression;
    private syntaxError: ISyntaxCheckResult;

    constructor(equation: string){
        this.equationRaw = equation;
        const _tokenize = this.tokenize(equation);
        this.allTokens = _tokenize.allTokens;
        this.tokens = _tokenize.token;
        this.equationStack = this.parse(this.tokens);
        this.syntaxError = this.validateEquationSyntax(this.allTokens, this.tokens, this.equationRaw);
    }

    getEquation(){
        return this.equationRaw;
    }

    getEquationStack(){
        return this.equationStack;
    }

    getHasError(){
        return this.syntaxError.index;
    }

    // Human-readable reason for the current syntax error, or '' when there isn't one — the
    // specific condition that failed (e.g. "Operator 'AND' requires at least one parameter."),
    // not just a generic "syntax error" label.
    getSyntaxErrorMessage(){
        return this.syntaxError.message;
    }

    private validateEquationParenthesisSyntax(tokens: string[]) {


        let stack = [];
        let prevToken = null;

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];

            if (token === '(') {
                // Push opening parenthesis onto the stack
                stack.push(token);
            } else if (token === ')') {
                // Pop an opening parenthesis from the stack
                if (stack.pop() !== '(') {
                    return i + 1; // Mismatched parentheses
                }
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            prevToken = token;
        }

        // Check if there are any unmatched opening parentheses
        return stack.length === 0 ? 0 : tokens.length + 1; // Unclosed parenthesis
    }

    private validateEquationSyntax(allTokens:string[], tokens:string[], equation: string): ISyntaxCheckResult {
        const operatorPattern = /^(AND|QAND|OR|QOR|IMPORT|EQUAL|NOTEQUAL|COMPARE|QHASANSWER|QHASNOTANSWER)$/;
        const noError: ISyntaxCheckResult = { index: 0, message: '' };
        if (!allTokens || equation === '') {
            return noError;
        }

        if(this.validateEquationParenthesisSyntax(tokens)>0){
            return { index: allTokens.length + 1, message: "Mismatched or unclosed parentheses — every '(' needs a matching ')'." };
        }

        let prevToken = null;

        for (let i = 0; i < allTokens.length; i++) {
            const token = allTokens[i];
            const nextToken = allTokens[i + 1];



            if(i === 0 &&  !operatorPattern.test(token)){
                return { index: i + 1, message: `Equation must start with a valid operator (AND, OR, QAND, QOR, IMPORT, EQUAL, NOTEQUAL, COMPARE, QHASANSWER, QHASNOTANSWER) — found '${token}'.` };
            }
            else if(!nextToken){
                if(token !== ')'){
                    return { index: i + 1, message: `Equation must end with a closing parenthesis ')' — found '${token}'.` };
                }
            }
            else if (token === '(') {
                if (!prevToken || !operatorPattern.test(prevToken)) {
                    return { index: i + 1, message: "'(' must immediately follow an operator name." };
                }
                else if(nextToken === ')'){
                    return { index: i + 1, message: `Operator '${prevToken}' requires at least one parameter — found empty '()'.` };
                }

            } else if (token === ')') {

            } else if (operatorPattern.test(token)) {
                if(prevToken === ')'){
                    return { index: i + 1, message: `Operator '${token}' cannot immediately follow a closing parenthesis ')' — check for a missing AND/OR wrapping the expressions.` };
                }
                // Operators should be followed by opening parenthesis or another operator
                else if (nextToken !== '(') {
                    return { index: i + 1, message: `Operator '${token}' must be immediately followed by an opening parenthesis '('.` };
                }
                else if(operatorPattern.test(nextToken)){
                    return { index: i + 1, message: `Operator '${token}' cannot be immediately followed by another operator name '${nextToken}'.` };
                }

            }else if(token === ','){
                if(!nextToken){
                    return { index: i + 1, message: "Equation cannot end with a comma ','." };
                }
                else if(nextToken === ',' || prevToken === ','){
                    return { index: i + 1, message: "Consecutive commas ',,' are not allowed." };
                }
                else if(prevToken === '(' || nextToken === ')'){
                    return { index: i + 1, message: "Empty argument found next to a comma — check for a missing value between '(' / ',' / ')'." };
                }

            }

            prevToken = token;
        }

        const parseArr = this.parse(tokens);
        if(parseArr.length !== 2 ){
            return { index: allTokens.length + 1, message: "Equation structure is invalid — check that every operator has exactly one argument list." };
        }

        return noError;
    }

    // Trims whitespace, then strips ONE matching pair of surrounding quotes if present (e.g.
    // "5" or '5' -> 5) — lets a user optionally wrap a COMPARE/EQUAL value (or any other
    // argument) in quotes without it becoming part of the literal comparison text, since none of
    // the operators (operatorEQUAL/operatorNOTEQUAL/operatorCOMPARE) strip quotes themselves.
    // Only strips when the WHOLE trimmed token starts and ends with the same quote character, so
    // this can't accidentally mangle unrelated content.
    private normalizeToken(token: string): string {
        const trimmed = token.trim();
        if (
            trimmed.length >= 2 &&
            ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
                (trimmed.startsWith("'") && trimmed.endsWith("'")))
        ) {
            return trimmed.slice(1, -1);
        }
        return trimmed;
    }

    private tokenize(input: string) {
        const tokens:string[] = [];
        const allTokens:string[] = [];
        const operators: readonly string[] = RQL_OPERATORS;
        let currentToken = '';

        for (let i = 0; i < input.length; i++) {
            const char = input[i];

            if (char === '(') {
                if (currentToken) {
                    tokens.push(this.normalizeToken(currentToken));
                    allTokens.push(this.normalizeToken(currentToken));
                    currentToken = '';
                }
                tokens.push('(');
                allTokens.push('(');
            } else if (char === ')') {
                if (currentToken) {
                    tokens.push(this.normalizeToken(currentToken));
                    allTokens.push(this.normalizeToken(currentToken));
                    currentToken = '';
                }
                tokens.push(')');
                allTokens.push(')');
            } else if (char === ',') {
                if (currentToken) {
                    tokens.push(this.normalizeToken(currentToken));
                    allTokens.push(this.normalizeToken(currentToken));
                    currentToken = '';
                }
                allTokens.push(char.trim());
            } else {
                currentToken += char;
                if (i === input.length - 1) {
                    tokens.push(this.normalizeToken(currentToken));
                    allTokens.push(this.normalizeToken(currentToken));
                }
            }
        }

        return{
            token: tokens.filter(token => operators.includes(token) || token !== ''),
            allTokens: allTokens.filter(token => operators.includes(token) || token !== '')
        };
    }

    private parse(tokens: string[]) {
        let currentToken = 0;
    
        function parseExpression() {
            let expr:ILogicalExpression = [];
            
            while (currentToken < tokens.length) {
                if (tokens[currentToken] === '(') {
                    currentToken++;
                    expr.push(parseExpression());
                } else if (tokens[currentToken] === ')') {
                    currentToken++;
                    return expr;
                } else {
                    const token = tokens[currentToken];
                    currentToken++;
                    expr.push(token);
                }
            }
    
            return expr;
        }
    
        return parseExpression();
    }
    
}